/**
 * dirMapPins — the national directory's doctors as map pins (s101 Phase 4; shared by the share
 * picker and the Doctors tab since s105). Each row carries lat/lng from /providers/search: the
 * office's street address when it is geocoded (`placed: 'address'`, s105), else its ZIP centroid.
 * Pure.
 */
// s101 Phase 4: many directory offices share one ZIP centroid; spread them on a small ring so each
// pin can be tapped (the finder's own spacing).
// ★ s104 (TestFlight b70, up to 250 directory doctors on the map): the ring index counts the offices
//   of THAT centroid, not the row's place in the whole list. With the global index the 250th pin sat
//   ~4 miles from its own ZIP.
import { officeKey } from './officeGroups.js'

// ★ s123 (Dr. Rami 2026-10-29, board F; measured on PROD: 73 clinicians at 401 Bicentennial Way drew 73 pins on one
//   spot): clinicians at the SAME confirmed street address are ONE pin — the nearest of them, carrying __count and
//   __officeRows (all of them, for the office's sheet). Rows placed at a ZIP centroid keep the ring below.
function collapseOffices(rows) {
  const groups = new Map()
  const list = []
  for (const r of rows) {
    if (r.placed === 'address') {
      const k = officeKey(r)
      if (groups.has(k)) { groups.get(k).push(r); continue }
      groups.set(k, [r])
    }
    list.push(r)
  }
  return list.map((r) => {
    const g = r.placed === 'address' ? groups.get(officeKey(r)) : null
    return g && g.length > 1 ? { ...r, __count: g.length, __officeRows: g } : r
  })
}

export function dirMapPins(rows) {
  const perCentroid = {}
  return collapseOffices((rows || [])
    .filter((r) => typeof r.lat === 'number' && typeof r.lng === 'number')
    // ★ s110 (Dr. Rami, TestFlight build 77: "Lets remove the ambers from the map view and only keep them on list
    //   view"): an office whose address is not confirmed is never drawn on a map. It stays in the list, and picking
    //   it there still opens its card.
    .filter((r) => !isAreaRow(r))
    // ★ s126 (TestFlight build 92, Dr. Rami: "Griffith park in LA is full of listings. Cant be correct"; "Seeing offices
    //   in residential areas again"): an office whose street address has no geocode is placed by the API at its ZIP's
    //   CENTRE (placed: 'zip'), and the ring below spread every such office around that point — 90027's centre is in
    //   Griffith Park, so dozens of pins landed in the park and on the streets around it. A pin claims a street; a ZIP
    //   centre is not one. Only offices at their own geocoded address are drawn; the rest stay in the list (their
    //   distance from the ZIP is still shown there), exactly as unconfirmed offices do since s110.
    .filter((r) => r.placed === 'address'))
    .map((r) => {
      const g = `${r.lat},${r.lng}`
      const j = perCentroid[g] = (perCentroid[g] === undefined ? 0 : perCentroid[g] + 1)
      // s105: offices at their own street address share a point only when they share a building, so they
      //   spread on a ring ~40 m wide; ZIP-centroid pins keep the wider ring.
      const k = dirPinJitter(j, r.placed === 'address' ? 0.0004 : 0.0025)
      // s110: an address not confirmed is an AREA (drawn as a dashed circle by DoctorsMap), never a street pin.
      return { ...r, id: `npi-${r.npi}`, __directory: true, __area: r.addressConfirmed === false || r.placed === 'area',
               practice_lat: r.lat + k[0], practice_lng: r.lng + k[1] }
    })
}
export function isAreaRow(r) {
  return !!r && (r.addressConfirmed === false || r.placed === 'area')
}
export function dirPinJitter(i, step = 0.0025) {
  if (i === 0) return [0, 0]
  const a = (i * 137.5) * Math.PI / 180
  const r = step * Math.sqrt(i)
  return [r * Math.cos(a), r * Math.sin(a)]
}
